/**
 * Helpers visuais compartilhados pelos relatórios: formatação de números
 * pt-BR, minutos como h:mm, link teal do ticket e chip de % de SLA.
 */
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/misc';

// ── Formatação ──────────────────────────────────────────────────────────────

/**
 * 90 → "1:30" (horas sem zero à esquerda, minutos com dois dígitos). Mora no
 * shared desde o extrato em PDF: a tela e o documento escrevem a mesma hora.
 */
export { minutesToHmm } from '@erp/shared';

/** Decimal pt-BR (vírgula), até `digits` casas; null → "—". */
export function fmtDecimal(value: number | null, digits = 1): string {
  if (value === null) return '—';
  return value.toLocaleString('pt-BR', { maximumFractionDigits: digits });
}

/** "92,3%"; null → "—". */
export function fmtPct(value: number | null): string {
  if (value === null) return '—';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

/** Decimal com vírgula para células de CSV; null → ''. */
export function csvDecimal(value: number | null, digits = 1): string {
  if (value === null) return '';
  return value.toFixed(digits).replace('.', ',');
}

export const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

// ── Estilos de tabela (padrão contratos/TicketList) ─────────────────────────
export const TH = 'h-9 px-4 text-left text-[13px] font-semibold whitespace-nowrap';
export const TH_NUM = 'h-9 px-4 text-right text-[13px] font-semibold whitespace-nowrap';
export const TD_NUM = 'px-4 text-right tabular-nums whitespace-nowrap';

// ── Link teal do ticket ─────────────────────────────────────────────────────
export function TicketLink({ number }: { number: number }) {
  return (
    <Link
      to="/tickets/$ticketNumber"
      params={{ ticketNumber: String(number) }}
      className="font-medium text-primary tabular-nums hover:text-primary-pressed hover:underline"
    >
      #{number}
    </Link>
  );
}

// ── Chip de percentual de SLA (verde ≥90, amarelo ≥70, vermelho abaixo) ─────
export function PctChip({ value }: { value: number | null }) {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  const variant = value >= 90 ? 'success' : value >= 70 ? 'warning' : 'destructive';
  return (
    <Badge variant={variant} className="tabular-nums">
      {fmtPct(value)}
    </Badge>
  );
}
